'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface NewsArticle {
  id: string;
  category: string;
  categoryColor: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  author: string;
  featured?: boolean;
}

const newsArticles: NewsArticle[] = [
  {
    id: 'news-001',
    category: 'Market Report',
    categoryColor: 'bg-green-100 text-green-800',
    title: 'UK Store Cattle Prices Hold Firm Amid Tight Supply at Autumn Sales',
    excerpt: 'Store cattle prices remained resilient across major UK markets this week, with Limousin and Charolais crosses attracting strong bidding from graziers. Tight supply continues to underpin values heading into the back-end season.',
    date: '2 Aug 2026',
    readTime: '3 min read',
    author: 'FarmTradeDirect',
    featured: true,
  },
  {
    id: 'news-002',
    category: 'Industry News',
    categoryColor: 'bg-amber-100 text-amber-800',
    title: 'Red Tractor Announces Updated Assurance Standards for 2027',
    excerpt: 'Red Tractor has published its revised farm assurance standards, with new requirements around antibiotic stewardship and environmental benchmarking coming into force from January 2027.',
    date: '1 Aug 2026',
    readTime: '4 min read',
    author: 'FarmTradeDirect',
  },
  {
    id: 'news-003',
    category: 'Auction Results',
    categoryColor: 'bg-blue-100 text-blue-800',
    title: 'Skipton Sheep Sale: Texel Shearlings Top at £320 Per Head',
    excerpt: 'This week\'s Skipton prime sheep sale saw Texel shearlings reach a top price of £320 per head, with a strong trade reported throughout. Overall throughput was up 12% on the same week last year.',
    date: '31 Jul 2026',
    readTime: '2 min read',
    author: 'FarmTradeDirect',
  },
  {
    id: 'news-004',
    category: 'Policy & Regulation',
    categoryColor: 'bg-purple-100 text-purple-800',
    title: 'DEFRA Confirms Sustainable Farming Incentive Payment Rates for 2026',
    excerpt: 'DEFRA has confirmed the Sustainable Farming Incentive (SFI) payment rates for the 2026 scheme year, with several new actions added covering precision farming and hedgerow management.',
    date: '30 Jul 2026',
    readTime: '5 min read',
    author: 'FarmTradeDirect',
  },
  {
    id: 'news-005',
    category: 'Machinery',
    categoryColor: 'bg-orange-100 text-orange-800',
    title: 'Second-Hand Tractor Values Stabilise After Two Years of Decline',
    excerpt: 'The used agricultural machinery market is showing signs of stabilisation, with tractor values levelling off after a prolonged correction period. Dealers report improved buyer confidence at recent machinery sales.',
    date: '29 Jul 2026',
    readTime: '3 min read',
    author: 'FarmTradeDirect',
  },
];

export default function NewsUpdatesSection() {
  const featured = newsArticles.find((a) => a.featured);
  const rest = newsArticles.filter((a) => !a.featured).slice(0, 4);

  return (
    <section className="bg-[#f8f5f0] border-t border-border py-12 px-4">
      <div className="max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-1 h-8 rounded-full bg-primary" />
            <div>
              <h2 className="text-2xl font-bold text-foreground tracking-tight">News &amp; Updates</h2>
              <p className="text-sm text-muted-foreground mt-0.5">Market reports, industry news &amp; auction results</p>
            </div>
          </div>
          <Link
            href="/news-updates"
            className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            View all news
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Featured article */}
          {featured && (
            <Link
              href={`/news-updates#${featured.id}`}
              className="lg:col-span-3 group bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5 flex flex-col"
            >
              <div className="bg-gradient-to-br from-primary/90 to-primary/60 p-8 flex-1 flex flex-col justify-between min-h-[220px]">
                <div>
                  <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full mb-4 ${featured.categoryColor}`}>
                    {featured.category}
                  </span>
                  <h3 className="text-xl font-bold text-white leading-snug group-hover:text-green-100 transition-colors">
                    {featured.title}
                  </h3>
                  <p className="text-green-100/80 text-sm mt-3 leading-relaxed line-clamp-3">
                    {featured.excerpt}
                  </p>
                </div>
                <div className="flex items-center gap-3 mt-6 text-green-200/70 text-xs font-medium">
                  <span className="flex items-center gap-1">
                    <Icon name="CalendarDaysIcon" size={13} />
                    {featured.date}
                  </span>
                  <span className="w-1 h-1 rounded-full bg-green-200/40" />
                  <span className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={13} />
                    {featured.readTime}
                  </span>
                  <span className="ml-auto flex items-center gap-1 text-green-300 font-semibold">
                    Read more <Icon name="ArrowRightIcon" size={13} />
                  </span>
                </div>
              </div>
            </Link>
          )}

          {/* Side articles */}
          <div className="lg:col-span-2 flex flex-col gap-3">
            {rest.map((article) => (
              <Link
                key={article.id}
                href={`/news-updates#${article.id}`}
                className="group bg-card border border-border rounded-xl p-4 hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 flex flex-col gap-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${article.categoryColor}`}>
                    {article.category}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                    <Icon name="CalendarDaysIcon" size={12} />
                    {article.date}
                  </span>
                </div>
                <h3 className="text-sm font-bold text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2">
                  {article.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed line-clamp-2">
                  {article.excerpt}
                </p>
                <span className="text-xs font-semibold text-primary flex items-center gap-1 mt-auto">
                  Read more <Icon name="ArrowRightIcon" size={12} />
                </span>
              </Link>
            ))}
          </div>
        </div>

        {/* Mobile view all */}
        <div className="mt-6 sm:hidden text-center">
          <Link href="/news-updates" className="btn-outline text-sm py-2 px-5">
            View all news &amp; updates
          </Link>
        </div>
      </div>
    </section>
  );
}
